import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { DialogTitle } from "@radix-ui/react-dialog";
import { Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { apiErrorMessage } from "service/moneeflo/client";
import {
  DailyRateRecipient,
  DailyRateRow,
  getDailyRatesDraft,
  sendDailyRates,
} from "service/moneeflo/daily-rates-api";
import { toast } from "sonner";

/**
 * Send Daily Rates, in two steps.
 *
 * Step one is the sheet: today's date and a row per currency with what we
 * buy at, sell at, and load a travel card at. It opens prefilled with the
 * last sheet this organization sent. Step two is who it goes to: every
 * corporate lead the desk can see, all ticked, each with its email. Leads
 * without an email are shown but cannot be ticked. The desk unticks any
 * it does not want, confirms, and the API sends one message per lead.
 */

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const EMPTY_ROW: DailyRateRow = { currency: "", buy: 0, sell: 0, card: 0 };

const formatDate = (iso: string): string => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

export default function DailyRatesDialog({ open, onOpenChange }: Props) {
  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [date, setDate] = useState("");
  const [lastSentAt, setLastSentAt] = useState<string | null>(null);
  const [rows, setRows] = useState<DailyRateRow[]>([]);
  const [note, setNote] = useState("");
  const [recipients, setRecipients] = useState<DailyRateRecipient[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setConfirming(false);
    setSearch("");
    setLoading(true);
    getDailyRatesDraft()
      .then((draft) => {
        setDate(draft.date);
        setLastSentAt(draft.lastSentAt);
        setRows(draft.rates.length ? draft.rates : [EMPTY_ROW]);
        setNote("");
        setRecipients(draft.recipients);
        // Everyone with an address starts ticked; the desk removes, not adds.
        setSelected(
          new Set(draft.recipients.filter((r) => r.email).map((r) => r.id)),
        );
      })
      .catch((e) => toast.error(apiErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [open]);

  const updateRow = (
    index: number,
    field: keyof DailyRateRow,
    value: string,
  ) => {
    setRows((prev) =>
      prev.map((row, i) =>
        i !== index
          ? row
          : field === "currency"
            ? { ...row, currency: value.toUpperCase().slice(0, 3) }
            : { ...row, [field]: value === "" ? 0 : Number(value) },
      ),
    );
  };

  const quotedRows = rows.filter(
    (r) => r.currency.length === 3 && (r.buy > 0 || r.sell > 0 || r.card > 0),
  );

  const goToRecipients = () => {
    const badCurrency = rows.find((r) => r.currency.trim() && r.currency.length !== 3);
    if (badCurrency) {
      toast.error(`"${badCurrency.currency}" is not a currency code`);
      return;
    }
    if (quotedRows.length === 0) {
      toast.error("Enter at least one rate before going on");
      return;
    }
    const codes = quotedRows.map((r) => r.currency);
    if (new Set(codes).size !== codes.length) {
      toast.error("A currency is listed twice");
      return;
    }
    setStep(2);
  };

  const visibleRecipients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.email ?? "").toLowerCase().includes(q) ||
        (r.city ?? "").toLowerCase().includes(q),
    );
  }, [recipients, search]);

  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const withEmail = recipients.filter((r) => r.email);
  const allTicked = withEmail.length > 0 && withEmail.every((r) => selected.has(r.id));

  const send = async () => {
    setSending(true);
    try {
      const result = await sendDailyRates({
        date,
        rates: quotedRows,
        organizationIds: [...selected],
        note: note.trim() || undefined,
      });
      const skippedLine = result.skipped.length
        ? ` ${result.skipped.length} skipped.`
        : "";
      toast.success(
        `Daily rates sent to ${result.sent} ${result.sent === 1 ? "lead" : "leads"}.${skippedLine}`,
      );
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSending(false);
      setConfirming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogOverlay className="fixed inset-0 bg-black/60" />
      <DialogContent className="max-w-4xl sm:max-w-2xl w-full p-0 h-[calc(100vh-100px)] bg-[#fff] rounded-lg shadow-lg flex flex-col">
        <div className="relative w-full p-8 pb-0 flex-1 overflow-auto">
          <DialogHeader className="mb-4 border-b border-gray-200 pb-3">
            <DialogTitle className="text-lg font-semibold text-gray-800 text-left">
              Send Daily Rates
            </DialogTitle>
            <ol className="mt-3 flex items-center gap-3 text-sm">
              <StepPill n={1} label="Rates" active={step === 1} done={step > 1} />
              <span className="h-px w-8 bg-gray-300" />
              <StepPill n={2} label="Recipients" active={step === 2} done={false} />
            </ol>
          </DialogHeader>

          {step === 1 ? (
            <>
              <div className="mb-4 flex flex-row justify-between items-center">
                <div>
                  <label className="block text-sm font-medium text-[#666666]">
                    Date
                  </label>
                  {lastSentAt && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Prefilled from the last sheet sent on{" "}
                      {new Date(lastSentAt).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                      . Check every figure before sending.
                    </p>
                  )}
                </div>
                <div className="text-sm text-[#333333] bg-gray-100 rounded-md px-3 py-2">
                  {date ? formatDate(date) : "…"}
                </div>
              </div>

              <div className="border border-gray-200 rounded-[8px] overflow-hidden">
                <div className="grid grid-cols-[110px_1fr_1fr_1fr_40px] gap-3 bg-[#165FE3] text-[#fff] text-sm font-medium px-4 py-2">
                  <div>CCY</div>
                  <div>We buy (₹)</div>
                  <div>We sell (₹)</div>
                  <div>Travel card (₹)</div>
                  <div />
                </div>
                <div className="max-h-[340px] overflow-auto">
                  {rows.map((row, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-[110px_1fr_1fr_1fr_40px] gap-3 px-4 py-2.5 bg-gray-50 border-t border-gray-100 items-center"
                    >
                      <input
                        type="text"
                        value={row.currency}
                        onChange={(e) => updateRow(index, "currency", e.target.value)}
                        className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white uppercase"
                        placeholder="USD"
                        maxLength={3}
                      />
                      {(["buy", "sell", "card"] as const).map((field) => (
                        <input
                          key={field}
                          type="number"
                          inputMode="decimal"
                          min={0}
                          step="0.01"
                          value={row[field] === 0 ? "" : row[field]}
                          onChange={(e) => updateRow(index, field, e.target.value)}
                          className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white text-right"
                          placeholder="—"
                        />
                      ))}
                      <button
                        aria-label={`Remove ${row.currency || "row"}`}
                        className="text-gray-400 hover:text-red-500 justify-self-center"
                        onClick={() =>
                          setRows((prev) =>
                            prev.length === 1 ? [EMPTY_ROW] : prev.filter((_, i) => i !== index),
                          )
                        }
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setRows((prev) => [...prev, EMPTY_ROW])}
                className="mt-3 text-sm text-[#165FE3] font-medium flex items-center gap-1"
              >
                <span className="w-[17px] h-[17px] rounded-full border border-[#165FE3] flex items-center justify-center text-[13px] leading-none">
                  +
                </span>
                Add Row
              </button>

              <div className="mt-5">
                <label className="block text-sm font-medium text-[#666666]">
                  A line for today (optional)
                </label>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 600))}
                  className="mt-1 w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-white"
                  placeholder="e.g. USD has eased this morning; a good day to cover import payments."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Shown above the rates table. Leave empty to send the table alone.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-[#333333]">
                  Rates for <span className="font-medium">{formatDate(date)}</span> go to{" "}
                  <span className="font-semibold">{selected.size}</span> of {withEmail.length}{" "}
                  leads with an email.
                  {recipients.length - withEmail.length > 0 && (
                    <span className="text-gray-500">
                      {" "}
                      {recipients.length - withEmail.length} more have no email on file.
                    </span>
                  )}
                </p>
                <button
                  className="text-sm text-[#165FE3] font-medium"
                  onClick={() =>
                    setSelected(allTicked ? new Set() : new Set(withEmail.map((r) => r.id)))
                  }
                >
                  {allTicked ? "Remove all" : "Select all"}
                </button>
              </div>

              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-md pl-9 pr-3 py-2 bg-white"
                  placeholder="Search by company, email or city"
                />
              </div>

              <div className="border border-gray-200 rounded-[8px] overflow-hidden">
                <div className="grid grid-cols-[32px_1.3fr_1.4fr_110px] gap-3 bg-gray-100 text-[#333333] text-xs font-semibold uppercase tracking-wide px-4 py-2">
                  <div />
                  <div>Company</div>
                  <div>Email</div>
                  <div>Status</div>
                </div>
                <div className="max-h-[420px] overflow-auto">
                  {visibleRecipients.length === 0 && (
                    <p className="px-4 py-6 text-sm text-gray-500">
                      {recipients.length === 0
                        ? "No corporate leads to send to."
                        : "Nothing matches that search."}
                    </p>
                  )}
                  {visibleRecipients.map((r) => {
                    const canSend = !!r.email;
                    const on = selected.has(r.id);
                    return (
                      <label
                        key={r.id}
                        className={`grid grid-cols-[32px_1.3fr_1.4fr_110px] gap-3 px-4 py-2.5 border-t border-gray-100 items-center text-sm ${
                          canSend ? "cursor-pointer hover:bg-gray-50" : "text-gray-400"
                        } ${on ? "bg-[#F5F9FF]" : ""}`}
                      >
                        <Checkbox
                          checked={on}
                          disabled={!canSend}
                          onCheckedChange={(v) => toggle(r.id, v === true)}
                        />
                        <div className="truncate">
                          <div className="font-medium truncate">{r.name}</div>
                          {r.city && <div className="text-xs text-gray-500 truncate">{r.city}</div>}
                        </div>
                        <div className="truncate">
                          {r.email ?? <span className="italic">No email on file</span>}
                        </div>
                        <div className="capitalize text-xs">{r.status}</div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="p-6 rounded-b-lg bg-[#F9F9F9] flex-row justify-between items-center gap-2 border-t">
          <div className="text-xs text-gray-500">
            {step === 1
              ? `${quotedRows.length} ${quotedRows.length === 1 ? "currency" : "currencies"} quoted`
              : confirming
                ? `Send to ${selected.size} ${selected.size === 1 ? "lead" : "leads"}? This cannot be undone.`
                : ""}
          </div>
          <div className="flex items-center gap-2">
            {step === 1 ? (
              <>
                <button
                  className="px-4 py-2 h-fit border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 h-fit bg-[#165FE3] text-[#fff] rounded-md text-sm hover:bg-blue-800 disabled:opacity-50"
                  onClick={goToRecipients}
                  disabled={loading}
                >
                  Next: choose recipients
                </button>
              </>
            ) : confirming ? (
              <>
                <button
                  className="px-4 py-2 h-fit border border-[#165FE3] text-[#165FE3] rounded-md text-sm hover:bg-gray-100"
                  onClick={() => setConfirming(false)}
                  disabled={sending}
                >
                  No, go back
                </button>
                <button
                  className="px-4 py-2 h-fit bg-[#165FE3] text-[#fff] rounded-md text-sm hover:bg-blue-800 disabled:opacity-50"
                  onClick={send}
                  disabled={sending}
                >
                  {sending ? "Sending…" : `Yes, send to ${selected.size}`}
                </button>
              </>
            ) : (
              <>
                <button
                  className="px-4 py-2 h-fit border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setStep(1)}
                >
                  Back
                </button>
                <button
                  className="px-4 py-2 h-fit bg-[#165FE3] text-[#fff] rounded-md text-sm hover:bg-blue-800 disabled:opacity-50"
                  onClick={() => setConfirming(true)}
                  disabled={selected.size === 0}
                >
                  Send to {selected.size}
                </button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepPill({
  n,
  label,
  active,
  done,
}: {
  n: number;
  label: string;
  active: boolean;
  done: boolean;
}) {
  return (
    <li className="flex items-center gap-2">
      <span
        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
          active || done ? "bg-[#165FE3] text-white" : "bg-gray-200 text-gray-600"
        }`}
      >
        {done ? "✓" : n}
      </span>
      <span className={active ? "font-medium text-[#333333]" : "text-gray-500"}>{label}</span>
    </li>
  );
}
